/**
 * ONE TAP — collision world.
 *
 * Runs identically in the Cloudflare Worker and in the browser. No DOM, no
 * `three`, no allocation in the hot path: every query writes into caller-supplied
 * or module-scope scratch.
 *
 * ---------------------------------------------------------------------------
 * THE KERNEL
 * ---------------------------------------------------------------------------
 * Every brush is a box with yaw-only rotation; every ramp is a box with
 * Ry(yaw) * Rx(pitch). So the whole arena is a soup of oriented boxes, and every
 * query (capsule sweep, sphere sweep, hitscan, overlap) reduces to ONE primitive:
 *
 *     ray  vs  rounded box, in the box's local space
 *
 * That reduction is exact, not an approximation, and it falls out of Minkowski
 * algebra:
 *
 *   Box (+) Capsule
 *     = Box (+) ( segment (+) sphere(r) )        a capsule IS a swept sphere
 *     = ( Box (+) segment ) (+) sphere(r)        (+) is associative
 *     = TallerBox (+) sphere(r)                  box (+) axis-aligned segment
 *     = rounded box                              box (+) sphere IS a rounded box
 *
 * A capsule sweep therefore becomes: march the *capsule centre* as a bare ray
 * against a box grown by the capsule's half-segment, with every edge and corner
 * rounded by the capsule radius. Faces, edges and corners are all handled by one
 * signed distance function, so contact normals are continuous — no popping as you
 * slide off the lip of a catwalk, which is exactly what wall-run and slope
 * classification need.
 *
 * The player capsule is world-vertical. For a yaw-only brush the capsule is also
 * vertical in local space, so `Box (+) segment` is a pure Y growth and the result
 * is *exact*. For a pitched ramp the segment tilts in local space, and we grow the
 * core box by the segment's per-axis extent |segment . axis|. Note what that
 * gives us: on the ramp's top and bottom faces — the only faces you ever surf —
 * the growth is hc*cos(pitch), which is precisely the true contact offset, so the
 * ramp surface is still exact. Only the ramp's four buried end/side faces are
 * conservatively fattened by up to hc*sin(pitch); every ramp in ARENA has those
 * faces sunk into a deck, so it is never observable. Conservative also means it
 * can only ever report contact early. It can never tunnel.
 *
 * ---------------------------------------------------------------------------
 * ROBUSTNESS
 * ---------------------------------------------------------------------------
 * - Face hits (the overwhelming majority: floors, walls, deck lips) take an exact
 *   analytic slab-clip path with zero iteration.
 * - Edge/corner hits fall back to sphere tracing on the exact rounded-box SDF.
 *   Sphere tracing is monotone and 1-Lipschitz: it *cannot* step through the
 *   surface, so there is no tunneling at any speed.
 * - Sphere tracing alone is NOT enough, and assuming it was is what used to let a
 *   player sink into the world. Its convergence is geometric, so a shallow path
 *   that closes on an edge — falling past the lip of a curb, sliding along a
 *   parapet — burns the whole iteration budget while still outside, and reporting
 *   that as a miss lets the capsule end the tick up to ~26 mm INSIDE the brush.
 *   The next tick then starts solid, and the player hitches. So an exhausted trace
 *   falls through to a bisection: the rounded box is convex, therefore the SDF
 *   along the segment is convex and has exactly one entry crossing, and the last
 *   sphere-trace step is a proven-outside bracket for it. That makes the rescue
 *   exact, not heuristic.
 * - Contacts are reported a hair (CONTACT_SKIN) before touching, so a body at rest
 *   never re-reports startSolid on the next tick. That is what kills resting jitter.
 * - startSolid pushes out along the shallowest local axis of the deepest overlap.
 *
 * ---------------------------------------------------------------------------
 * BROADPHASE
 * ---------------------------------------------------------------------------
 * Uniform 3D grid in CSR layout (two Int32Arrays, built once). Sweeps rasterise
 * their swept AABB; hitscans walk an Amanatides-Woo DDA and bail as soon as the
 * best hit is closer than the current voxel's exit. A per-query stamp array
 * de-dupes solids that straddle voxels without touching a Set.
 */

import { EPSILON, v3, type Vec3 } from '@shared/math';
import {
  SurfaceKind,
  type ArenaData,
  type CollisionWorld,
  type RaycastResult,
  type TraceResult,
} from '@shared/types';

// ---------------------------------------------------------------------------
// Tuning — geometry-solver constants only. Nothing here is a gameplay number.
// ---------------------------------------------------------------------------

/** Contacts stop this far short of the surface so a resting body never re-enters solid. */
const CONTACT_SKIN = 0.0015;
/**
 * A body must be THIS far inside a solid before we call it start-solid. Touching a
 * surface at exactly zero distance is resting, not penetrating; without this slop a
 * body that lands perfectly flush flickers between "hit" and "start solid" on
 * alternate ticks, which reads as jitter and, worse, makes prediction disagree
 * with the server over one float ulp.
 */
const PENETRATION_EPS = 1e-4;
/** Slop on the slab rejections so an exactly-grazing sweep resolves as a contact, not a miss. */
const SLAB_SLOP = 1e-9;
/** Sphere-tracing terminates once the SDF drops below this. */
const TRACE_TOL = 1e-4;
/** Hard iteration cap for sphere tracing. Exhausting it hands off to the bisection. */
const TRACE_MAX_ITERS = 48;
/** Bisection steps for the exhausted-trace rescue. 24 halvings of one tick of motion is sub-micron. */
const BISECT_ITERS = 24;
/** Broadphase voxel edge, metres. Sized against typical brush spans in ARENA. */
const CELL_SIZE = 8;
/** Grid is padded this far past the arena bounds so out-of-bounds queries clamp cleanly. */
const GRID_PAD = 4;

// Stride layout of the packed solid table (Float64Array).
const S_CX = 0;
const S_CY = 1;
const S_CZ = 2;
const S_HX = 3;
const S_HY = 4;
const S_HZ = 5;
const S_AXX = 6; // local +X axis in world space
const S_AXY = 7;
const S_AXZ = 8;
const S_AYX = 9; // local +Y axis in world space
const S_AYY = 10;
const S_AYZ = 11;
const S_AZX = 12; // local +Z axis in world space
const S_AZY = 13;
const S_AZZ = 14;
const S_UPX = 15; // |world-up expressed in local coords| — the segment growth basis
const S_UPY = 16;
const S_UPZ = 17;
const S_MINX = 18; // world AABB
const S_MINY = 19;
const S_MINZ = 20;
const S_MAXX = 21;
const S_MAXY = 22;
const S_MAXZ = 23;
const STRIDE = 24;

// ---------------------------------------------------------------------------
// Module-scope scratch. Written before read, never read across calls.
// ---------------------------------------------------------------------------

let _hitT = 0;
let _hitNx = 0;
let _hitNy = 0;
let _hitNz = 0;
let _hitPx = 0;
let _hitPy = 0;
let _hitPz = 0;
let _hitStartSolid = false;
/** Penetration depth when _hitStartSolid — used to pick which overlap to resolve first. */
let _hitDepth = 0;

const _scratchTrace: TraceResult = {
  fraction: 1,
  hit: false,
  normal: v3(0, 1, 0),
  point: v3(),
  surfaceId: -1,
  surface: SurfaceKind.Concrete,
  startSolid: false,
};

const _scratchRay: RaycastResult = {
  hit: false,
  distance: 0,
  point: v3(),
  normal: v3(0, 1, 0),
  surfaceId: -1,
  surface: SurfaceKind.Concrete,
};

// ---------------------------------------------------------------------------
// Rounded-box SDF, in local space. Core half extents (bx,by,bz), radius r.
// Classic exact form: length(max(|p| - b, 0)) + min(max component, 0) - r.
// We only need the exterior branch plus a sign, so it is split for speed.
// ---------------------------------------------------------------------------

function sdfRounded(
  px: number,
  py: number,
  pz: number,
  bx: number,
  by: number,
  bz: number,
  r: number,
): number {
  const qx = Math.abs(px) - bx;
  const qy = Math.abs(py) - by;
  const qz = Math.abs(pz) - bz;
  if (qx <= 0 && qy <= 0 && qz <= 0) {
    // Inside the core box: distance is the (negative) nearest face distance.
    return (qx > qy ? (qx > qz ? qx : qz) : qy > qz ? qy : qz) - r;
  }
  const ex = qx > 0 ? qx : 0;
  const ey = qy > 0 ? qy : 0;
  const ez = qz > 0 ? qz : 0;
  return Math.sqrt(ex * ex + ey * ey + ez * ez) - r;
}

// ---------------------------------------------------------------------------
// The one primitive: sweep a sphere-swept-segment against one solid.
//
// (sx,sy,sz)->(ex,ey,ez) is the path of the capsule CENTRE in world space.
// `hc` is the capsule's half segment length (height/2 - radius, clamped at 0);
// pass 0 for a sphere and 0/0 for a bare ray.
//
// Writes the module-scope _hit* slots. Returns true on contact or start-solid.
// ---------------------------------------------------------------------------

function sweepSolid(
  data: Float64Array,
  base: number,
  sx: number,
  sy: number,
  sz: number,
  dx: number,
  dy: number,
  dz: number,
  hc: number,
  radius: number,
  maxT: number,
): boolean {
  const cx = data[base + S_CX]!;
  const cy = data[base + S_CY]!;
  const cz = data[base + S_CZ]!;

  const axx = data[base + S_AXX]!;
  const axy = data[base + S_AXY]!;
  const axz = data[base + S_AXZ]!;
  const ayx = data[base + S_AYX]!;
  const ayy = data[base + S_AYY]!;
  const ayz = data[base + S_AYZ]!;
  const azx = data[base + S_AZX]!;
  const azy = data[base + S_AZY]!;
  const azz = data[base + S_AZZ]!;

  // Start and motion, in the solid's local frame.
  const rx = sx - cx;
  const ry = sy - cy;
  const rz = sz - cz;
  const lx = rx * axx + ry * axy + rz * axz;
  const ly = rx * ayx + ry * ayy + rz * ayz;
  const lz = rx * azx + ry * azy + rz * azz;
  const mx = dx * axx + dy * axy + dz * axz;
  const my = dx * ayx + dy * ayy + dz * ayz;
  const mz = dx * azx + dy * azy + dz * azz;

  // Core = brush half extents grown by the capsule segment's local extent.
  const bx = data[base + S_HX]! + hc * data[base + S_UPX]!;
  const by = data[base + S_HY]! + hc * data[base + S_UPY]!;
  const bz = data[base + S_HZ]! + hc * data[base + S_UPZ]!;

  // --- start solid ---------------------------------------------------------
  const d0 = sdfRounded(lx, ly, lz, bx, by, bz, radius);
  if (d0 < -PENETRATION_EPS) {
    // Shallowest axis of escape on the *outer* (radius-inflated) box.
    const ox = bx + radius - Math.abs(lx);
    const oy = by + radius - Math.abs(ly);
    const oz = bz + radius - Math.abs(lz);
    let nx = 0;
    let ny = 0;
    let nz = 0;
    let depth: number;
    if (ox <= oy && ox <= oz) {
      depth = ox;
      nx = lx >= 0 ? 1 : -1;
    } else if (oy <= oz) {
      depth = oy;
      ny = ly >= 0 ? 1 : -1;
    } else {
      depth = oz;
      nz = lz >= 0 ? 1 : -1;
    }
    _hitT = 0;
    _hitNx = nx * axx + ny * ayx + nz * azx;
    _hitNy = nx * axy + ny * ayy + nz * azy;
    _hitNz = nx * axz + ny * ayz + nz * azz;
    _hitPx = sx;
    _hitPy = sy;
    _hitPz = sz;
    _hitStartSolid = true;
    _hitDepth = depth;
    return true;
  }

  _hitStartSolid = false;

  // --- outer slab clip -----------------------------------------------------
  const ex = bx + radius;
  const ey = by + radius;
  const ez = bz + radius;

  let tMin = 0;
  let tMax = maxT;
  let entryAxis = -1;
  let entrySign = 1;

  // X slab
  if (mx > -EPSILON && mx < EPSILON) {
    if (lx < -ex || lx > ex) return false;
  } else {
    const inv = 1 / mx;
    let t1 = (-ex - lx) * inv;
    let t2 = (ex - lx) * inv;
    let sgn = -1;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
      sgn = 1;
    }
    if (t1 > tMin) {
      tMin = t1;
      entryAxis = 0;
      entrySign = sgn;
    }
    if (t2 < tMax) tMax = t2;
    if (tMin > tMax + SLAB_SLOP) return false;
  }
  // Y slab
  if (my > -EPSILON && my < EPSILON) {
    if (ly < -ey || ly > ey) return false;
  } else {
    const inv = 1 / my;
    let t1 = (-ey - ly) * inv;
    let t2 = (ey - ly) * inv;
    let sgn = -1;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
      sgn = 1;
    }
    if (t1 > tMin) {
      tMin = t1;
      entryAxis = 1;
      entrySign = sgn;
    }
    if (t2 < tMax) tMax = t2;
    if (tMin > tMax + SLAB_SLOP) return false;
  }
  // Z slab
  if (mz > -EPSILON && mz < EPSILON) {
    if (lz < -ez || lz > ez) return false;
  } else {
    const inv = 1 / mz;
    let t1 = (-ez - lz) * inv;
    let t2 = (ez - lz) * inv;
    let sgn = -1;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
      sgn = 1;
    }
    if (t1 > tMin) {
      tMin = t1;
      entryAxis = 2;
      entrySign = sgn;
    }
    if (t2 < tMax) tMax = t2;
    if (tMin > tMax + SLAB_SLOP) return false;
  }

  const motionLen = Math.sqrt(mx * mx + my * my + mz * mz);
  if (motionLen < EPSILON) return false;

  let tHit = -1;
  let lnx = 0;
  let lny = 0;
  let lnz = 0;

  // --- exact face path -----------------------------------------------------
  // If the outer-box entry point lies in a single face region (only the entry
  // axis is outside the core), the rounded box coincides with the outer box
  // there: the hit is exact, analytic, iteration-free.
  if (entryAxis >= 0) {
    const px = lx + mx * tMin;
    const py = ly + my * tMin;
    const pz = lz + mz * tMin;
    let outside = 0;
    if (Math.abs(px) > bx) outside++;
    if (Math.abs(py) > by) outside++;
    if (Math.abs(pz) > bz) outside++;
    if (outside <= 1) {
      tHit = tMin;
      if (entryAxis === 0) lnx = entrySign;
      else if (entryAxis === 1) lny = entrySign;
      else lnz = entrySign;
    }
  }

  // --- sphere trace for edge/corner regions --------------------------------
  if (tHit < 0) {
    let t = tMin;
    const invLen = 1 / motionLen;
    let found = false;
    // Last parameter proven to be OUTSIDE the surface. Sphere tracing never steps
    // past the surface, so this is always a valid lower bracket.
    let tSafe = tMin;
    for (let i = 0; i < TRACE_MAX_ITERS; i++) {
      const px = lx + mx * t;
      const py = ly + my * t;
      const pz = lz + mz * t;
      const d = sdfRounded(px, py, pz, bx, by, bz, radius);
      if (d < TRACE_TOL) {
        found = true;
        break;
      }
      tSafe = t;
      const next = t + d * invLen;
      if (next > tMax) {
        t = tMax;
        break;
      }
      t = next;
    }

    if (!found) {
      // Budget exhausted (or the slab exit reached) while still outside. If the far
      // end of the segment is inside, a crossing exists in (tSafe, tMax] and the SDF
      // is convex along the ray, so bisection lands on it exactly.
      const dEnd = sdfRounded(lx + mx * tMax, ly + my * tMax, lz + mz * tMax, bx, by, bz, radius);
      if (dEnd >= TRACE_TOL) return false;
      let lo = tSafe;
      let hi = tMax;
      for (let i = 0; i < BISECT_ITERS; i++) {
        const mid = (lo + hi) * 0.5;
        const d = sdfRounded(lx + mx * mid, ly + my * mid, lz + mz * mid, bx, by, bz, radius);
        if (d >= TRACE_TOL) lo = mid;
        else hi = mid;
      }
      t = hi;
    }
    tHit = t;
    const px = lx + mx * tHit;
    const py = ly + my * tHit;
    const pz = lz + mz * tHit;
    // Gradient of the SDF == outward normal.
    const qx = px < -bx ? -bx : px > bx ? bx : px;
    const qy = py < -by ? -by : py > by ? by : py;
    const qz = pz < -bz ? -bz : pz > bz ? bz : pz;
    let gx = px - qx;
    let gy = py - qy;
    let gz = pz - qz;
    const gl = Math.sqrt(gx * gx + gy * gy + gz * gz);
    if (gl > EPSILON) {
      const inv = 1 / gl;
      gx *= inv;
      gy *= inv;
      gz *= inv;
    } else {
      // Degenerate (radius 0 corner strike): fall back to the entry face.
      gx = entryAxis === 0 ? entrySign : 0;
      gy = entryAxis === 1 ? entrySign : 0;
      gz = entryAxis === 2 ? entrySign : 0;
    }
    lnx = gx;
    lny = gy;
    lnz = gz;
  }

  // Hold the contact a skin width short so a resting body never re-enters solid.
  tHit -= CONTACT_SKIN / motionLen;
  if (tHit < 0) tHit = 0;
  if (tHit > maxT) return false;

  // Contact point: nearest point on the *real* brush surface (undo the segment
  // growth on the way out so decals and impact VFX land on the geometry).
  const px = lx + mx * tHit;
  const py = ly + my * tHit;
  const pz = lz + mz * tHit;
  const hx = data[base + S_HX]!;
  const hy = data[base + S_HY]!;
  const hz = data[base + S_HZ]!;
  const cpx = px < -hx ? -hx : px > hx ? hx : px;
  const cpy = py < -hy ? -hy : py > hy ? hy : py;
  const cpz = pz < -hz ? -hz : pz > hz ? hz : pz;

  _hitT = tHit;
  _hitNx = lnx * axx + lny * ayx + lnz * azx;
  _hitNy = lnx * axy + lny * ayy + lnz * azy;
  _hitNz = lnx * axz + lny * ayz + lnz * azz;
  _hitPx = cx + cpx * axx + cpy * ayx + cpz * azx;
  _hitPy = cy + cpx * axy + cpy * ayy + cpz * azy;
  _hitPz = cz + cpx * axz + cpy * ayz + cpz * azz;
  return true;
}

/** Overlap-only variant of the same kernel. */
function overlapSolid(
  data: Float64Array,
  base: number,
  px: number,
  py: number,
  pz: number,
  hc: number,
  radius: number,
): boolean {
  const rx = px - data[base + S_CX]!;
  const ry = py - data[base + S_CY]!;
  const rz = pz - data[base + S_CZ]!;
  const lx = rx * data[base + S_AXX]! + ry * data[base + S_AXY]! + rz * data[base + S_AXZ]!;
  const ly = rx * data[base + S_AYX]! + ry * data[base + S_AYY]! + rz * data[base + S_AYZ]!;
  const lz = rx * data[base + S_AZX]! + ry * data[base + S_AZY]! + rz * data[base + S_AZZ]!;
  const bx = data[base + S_HX]! + hc * data[base + S_UPX]!;
  const by = data[base + S_HY]! + hc * data[base + S_UPY]!;
  const bz = data[base + S_HZ]! + hc * data[base + S_UPZ]!;
  return sdfRounded(lx, ly, lz, bx, by, bz, radius) < 0;
}

// ---------------------------------------------------------------------------
// The world
// ---------------------------------------------------------------------------

class Grid {
  readonly nx: number;
  readonly ny: number;
  readonly nz: number;
  readonly minX: number;
  readonly minY: number;
  readonly minZ: number;
  readonly inv: number;
  readonly cellStart: Int32Array;
  readonly cellItems: Int32Array;

  constructor(data: Float64Array, count: number, bmin: Vec3, bmax: Vec3) {
    this.minX = bmin.x - GRID_PAD;
    this.minY = bmin.y - GRID_PAD;
    this.minZ = bmin.z - GRID_PAD;
    this.inv = 1 / CELL_SIZE;
    this.nx = Math.max(1, Math.ceil((bmax.x + GRID_PAD - this.minX) * this.inv));
    this.ny = Math.max(1, Math.ceil((bmax.y + GRID_PAD - this.minY) * this.inv));
    this.nz = Math.max(1, Math.ceil((bmax.z + GRID_PAD - this.minZ) * this.inv));

    const cells = this.nx * this.ny * this.nz;
    const counts = new Int32Array(cells + 1);

    // Pass 1 — count.
    for (let i = 0; i < count; i++) {
      const b = i * STRIDE;
      const x0 = this.cx(data[b + S_MINX]!, this.nx, this.minX);
      const x1 = this.cx(data[b + S_MAXX]!, this.nx, this.minX);
      const y0 = this.cx(data[b + S_MINY]!, this.ny, this.minY);
      const y1 = this.cx(data[b + S_MAXY]!, this.ny, this.minY);
      const z0 = this.cx(data[b + S_MINZ]!, this.nz, this.minZ);
      const z1 = this.cx(data[b + S_MAXZ]!, this.nz, this.minZ);
      for (let z = z0; z <= z1; z++) {
        for (let y = y0; y <= y1; y++) {
          for (let x = x0; x <= x1; x++) {
            counts[(z * this.ny + y) * this.nx + x + 1]!++;
          }
        }
      }
    }
    for (let i = 0; i < cells; i++) counts[i + 1]! += counts[i]!;
    this.cellStart = counts;
    this.cellItems = new Int32Array(counts[cells]!);

    // Pass 2 — fill, using a rolling cursor copy so cellStart stays intact.
    const cursor = new Int32Array(cells);
    for (let i = 0; i < cells; i++) cursor[i] = counts[i]!;
    for (let i = 0; i < count; i++) {
      const b = i * STRIDE;
      const x0 = this.cx(data[b + S_MINX]!, this.nx, this.minX);
      const x1 = this.cx(data[b + S_MAXX]!, this.nx, this.minX);
      const y0 = this.cx(data[b + S_MINY]!, this.ny, this.minY);
      const y1 = this.cx(data[b + S_MAXY]!, this.ny, this.minY);
      const z0 = this.cx(data[b + S_MINZ]!, this.nz, this.minZ);
      const z1 = this.cx(data[b + S_MAXZ]!, this.nz, this.minZ);
      for (let z = z0; z <= z1; z++) {
        for (let y = y0; y <= y1; y++) {
          for (let x = x0; x <= x1; x++) {
            const c = (z * this.ny + y) * this.nx + x;
            this.cellItems[cursor[c]!++] = i;
          }
        }
      }
    }
  }

  private cx(v: number, n: number, origin: number): number {
    const i = Math.floor((v - origin) * this.inv);
    return i < 0 ? 0 : i >= n ? n - 1 : i;
  }

  clampX(v: number): number {
    return this.cx(v, this.nx, this.minX);
  }
  clampY(v: number): number {
    return this.cx(v, this.ny, this.minY);
  }
  clampZ(v: number): number {
    return this.cx(v, this.nz, this.minZ);
  }
  index(x: number, y: number, z: number): number {
    return (z * this.ny + y) * this.nx + x;
  }
}

class World implements CollisionWorld {
  readonly arena: ArenaData;
  private readonly data: Float64Array;
  private readonly ids: Int32Array;
  private readonly surfaces: Int32Array;
  private readonly count: number;
  private readonly grid: Grid;
  private readonly stamp: Int32Array;
  private queryId = 0;

  constructor(arena: ArenaData) {
    this.arena = arena;

    const brushes = arena.brushes;
    const ramps = arena.ramps;
    let n = 0;
    for (let i = 0; i < brushes.length; i++) if (!brushes[i]!.decorative) n++;
    n += ramps.length;

    this.count = n;
    this.data = new Float64Array(n * STRIDE);
    this.ids = new Int32Array(n);
    this.surfaces = new Int32Array(n);
    this.stamp = new Int32Array(n);

    let w = 0;
    for (let i = 0; i < brushes.length; i++) {
      const b = brushes[i]!;
      if (b.decorative) continue;
      this.writeSolid(w++, b.id, b.surface, b.pos, b.half, b.yaw, 0);
    }
    for (let i = 0; i < ramps.length; i++) {
      const r = ramps[i]!;
      this.writeSolid(w++, r.id, r.surface, r.pos, r.half, r.yaw, r.pitch);
    }

    this.grid = new Grid(this.data, this.count, arena.bounds.min, arena.bounds.max);
  }

  /** Bake one oriented box into the packed table. R = Ry(yaw) * Rx(pitch). */
  private writeSolid(
    slot: number,
    id: number,
    surface: SurfaceKind,
    pos: Vec3,
    half: Vec3,
    yaw: number,
    pitch: number,
  ): void {
    const d = this.data;
    const b = slot * STRIDE;
    const cy = Math.cos(yaw);
    const sy = Math.sin(yaw);
    const cp = Math.cos(pitch);
    const sp = Math.sin(pitch);

    // Columns of R: the solid's local axes expressed in world space.
    const axx = cy;
    const axy = 0;
    const axz = -sy;
    const ayx = sy * sp;
    const ayy = cp;
    const ayz = cy * sp;
    const azx = sy * cp;
    const azy = -sp;
    const azz = cy * cp;

    d[b + S_CX] = pos.x;
    d[b + S_CY] = pos.y;
    d[b + S_CZ] = pos.z;
    d[b + S_HX] = half.x;
    d[b + S_HY] = half.y;
    d[b + S_HZ] = half.z;
    d[b + S_AXX] = axx;
    d[b + S_AXY] = axy;
    d[b + S_AXZ] = axz;
    d[b + S_AYX] = ayx;
    d[b + S_AYY] = ayy;
    d[b + S_AYZ] = ayz;
    d[b + S_AZX] = azx;
    d[b + S_AZY] = azy;
    d[b + S_AZZ] = azz;

    // World up in local coordinates, absolute — the per-axis growth basis for the
    // capsule segment. For a yaw-only brush this is exactly (0, 1, 0).
    d[b + S_UPX] = Math.abs(axy);
    d[b + S_UPY] = Math.abs(ayy);
    d[b + S_UPZ] = Math.abs(azy);

    // World AABB of the oriented box.
    const ex = Math.abs(axx) * half.x + Math.abs(ayx) * half.y + Math.abs(azx) * half.z;
    const ey = Math.abs(axy) * half.x + Math.abs(ayy) * half.y + Math.abs(azy) * half.z;
    const ez = Math.abs(axz) * half.x + Math.abs(ayz) * half.y + Math.abs(azz) * half.z;
    d[b + S_MINX] = pos.x - ex;
    d[b + S_MINY] = pos.y - ey;
    d[b + S_MINZ] = pos.z - ez;
    d[b + S_MAXX] = pos.x + ex;
    d[b + S_MAXY] = pos.y + ey;
    d[b + S_MAXZ] = pos.z + ez;

    this.ids[slot] = id;
    this.surfaces[slot] = surface;
  }

  // -------------------------------------------------------------------------
  // Public queries
  // -------------------------------------------------------------------------

  traceCapsule(
    start: Vec3,
    end: Vec3,
    radius: number,
    height: number,
    out?: TraceResult,
  ): TraceResult {
    const hc = Math.max(height * 0.5 - radius, 0);
    return this.sweep(
      start.x,
      start.y + height * 0.5,
      start.z,
      end.x,
      end.y + height * 0.5,
      end.z,
      hc,
      radius,
      radius + hc,
      out ?? _scratchTrace,
    );
  }

  traceSphere(start: Vec3, end: Vec3, radius: number, out?: TraceResult): TraceResult {
    return this.sweep(
      start.x,
      start.y,
      start.z,
      end.x,
      end.y,
      end.z,
      0,
      radius,
      radius,
      out ?? _scratchTrace,
    );
  }

  raycast(origin: Vec3, dir: Vec3, maxDist: number, out?: RaycastResult): RaycastResult {
    const res = out ?? _scratchRay;
    res.hit = false;
    res.distance = maxDist;
    res.surfaceId = -1;
    res.surface = SurfaceKind.Concrete;
    res.normal.x = 0;
    res.normal.y = 1;
    res.normal.z = 0;
    res.point.x = origin.x + dir.x * maxDist;
    res.point.y = origin.y + dir.y * maxDist;
    res.point.z = origin.z + dir.z * maxDist;

    const len = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
    if (len < EPSILON || maxDist <= 0) return res;
    const dx = (dir.x / len) * maxDist;
    const dy = (dir.y / len) * maxDist;
    const dz = (dir.z / len) * maxDist;

    const qid = ++this.queryId;
    const g = this.grid;
    const data = this.data;

    let best = 1;
    let bestSlot = -1;
    let bnx = 0;
    let bny = 1;
    let bnz = 0;
    let bpx = 0;
    let bpy = 0;
    let bpz = 0;

    // Amanatides-Woo DDA. Bail as soon as the best hit precedes the voxel we are
    // about to enter — that is what keeps a 500m bullet trace cheap.
    let ix = g.clampX(origin.x);
    let iy = g.clampY(origin.y);
    let iz = g.clampZ(origin.z);

    const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
    const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;
    const stepZ = dz > 0 ? 1 : dz < 0 ? -1 : 0;

    const cell = CELL_SIZE;
    const bx0 = g.minX + ix * cell;
    const by0 = g.minY + iy * cell;
    const bz0 = g.minZ + iz * cell;

    let tMaxX =
      stepX === 0 ? Infinity : ((stepX > 0 ? bx0 + cell : bx0) - origin.x) / dx;
    let tMaxY =
      stepY === 0 ? Infinity : ((stepY > 0 ? by0 + cell : by0) - origin.y) / dy;
    let tMaxZ =
      stepZ === 0 ? Infinity : ((stepZ > 0 ? bz0 + cell : bz0) - origin.z) / dz;
    if (tMaxX < 0) tMaxX = 0;
    if (tMaxY < 0) tMaxY = 0;
    if (tMaxZ < 0) tMaxZ = 0;

    const tDeltaX = stepX === 0 ? Infinity : Math.abs(cell / dx);
    const tDeltaY = stepY === 0 ? Infinity : Math.abs(cell / dy);
    const tDeltaZ = stepZ === 0 ? Infinity : Math.abs(cell / dz);

    for (let guard = 0; guard < 4096; guard++) {
      const c = g.index(ix, iy, iz);
      const s0 = g.cellStart[c]!;
      const s1 = g.cellStart[c + 1]!;
      for (let k = s0; k < s1; k++) {
        const slot = g.cellItems[k]!;
        if (this.stamp[slot] === qid) continue;
        this.stamp[slot] = qid;
        if (
          sweepSolid(data, slot * STRIDE, origin.x, origin.y, origin.z, dx, dy, dz, 0, 0, best)
        ) {
          if (_hitStartSolid) {
            best = 0;
            bestSlot = slot;
            bnx = _hitNx;
            bny = _hitNy;
            bnz = _hitNz;
            bpx = origin.x;
            bpy = origin.y;
            bpz = origin.z;
            break;
          }
          if (_hitT < best) {
            best = _hitT;
            bestSlot = slot;
            bnx = _hitNx;
            bny = _hitNy;
            bnz = _hitNz;
            bpx = _hitPx;
            bpy = _hitPy;
            bpz = _hitPz;
          }
        }
      }

      const tNext = tMaxX < tMaxY ? (tMaxX < tMaxZ ? tMaxX : tMaxZ) : tMaxY < tMaxZ ? tMaxY : tMaxZ;
      if (bestSlot >= 0 && best <= tNext) break;
      if (tNext > 1 || tNext === Infinity) break;

      if (tMaxX < tMaxY) {
        if (tMaxX < tMaxZ) {
          ix += stepX;
          if (ix < 0 || ix >= g.nx) break;
          tMaxX += tDeltaX;
        } else {
          iz += stepZ;
          if (iz < 0 || iz >= g.nz) break;
          tMaxZ += tDeltaZ;
        }
      } else if (tMaxY < tMaxZ) {
        iy += stepY;
        if (iy < 0 || iy >= g.ny) break;
        tMaxY += tDeltaY;
      } else {
        iz += stepZ;
        if (iz < 0 || iz >= g.nz) break;
        tMaxZ += tDeltaZ;
      }
    }

    if (bestSlot < 0) return res;

    const nl = Math.sqrt(bnx * bnx + bny * bny + bnz * bnz);
    const inv = nl > EPSILON ? 1 / nl : 0;
    res.hit = true;
    res.distance = best * maxDist;
    res.point.x = bpx;
    res.point.y = bpy;
    res.point.z = bpz;
    res.normal.x = bnx * inv;
    res.normal.y = bny * inv;
    res.normal.z = bnz * inv;
    res.surfaceId = this.ids[bestSlot]!;
    res.surface = this.surfaces[bestSlot]! as SurfaceKind;
    return res;
  }

  isSolid(pos: Vec3, radius: number, height: number): boolean {
    const hc = Math.max(height * 0.5 - radius, 0);
    const px = pos.x;
    const py = pos.y + height * 0.5;
    const pz = pos.z;
    const r = radius + hc;

    const g = this.grid;
    const x0 = g.clampX(px - r);
    const x1 = g.clampX(px + r);
    const y0 = g.clampY(py - r);
    const y1 = g.clampY(py + r);
    const z0 = g.clampZ(pz - r);
    const z1 = g.clampZ(pz + r);
    const qid = ++this.queryId;

    for (let z = z0; z <= z1; z++) {
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const c = g.index(x, y, z);
          const s0 = g.cellStart[c]!;
          const s1 = g.cellStart[c + 1]!;
          for (let k = s0; k < s1; k++) {
            const slot = g.cellItems[k]!;
            if (this.stamp[slot] === qid) continue;
            this.stamp[slot] = qid;
            if (overlapSolid(this.data, slot * STRIDE, px, py, pz, hc, radius)) return true;
          }
        }
      }
    }
    return false;
  }

  // -------------------------------------------------------------------------
  // Shared sweep driver
  // -------------------------------------------------------------------------

  private sweep(
    sx: number,
    sy: number,
    sz: number,
    ex: number,
    ey: number,
    ez: number,
    hc: number,
    radius: number,
    pad: number,
    res: TraceResult,
  ): TraceResult {
    const dx = ex - sx;
    const dy = ey - sy;
    const dz = ez - sz;

    res.fraction = 1;
    res.hit = false;
    res.startSolid = false;
    res.surfaceId = -1;
    res.surface = SurfaceKind.Concrete;
    res.normal.x = 0;
    res.normal.y = 1;
    res.normal.z = 0;
    res.point.x = ex;
    res.point.y = ey;
    res.point.z = ez;

    const g = this.grid;
    const p = pad + 0.01;
    const x0 = g.clampX(Math.min(sx, ex) - p);
    const x1 = g.clampX(Math.max(sx, ex) + p);
    const y0 = g.clampY(Math.min(sy, ey) - p);
    const y1 = g.clampY(Math.max(sy, ey) + p);
    const z0 = g.clampZ(Math.min(sz, ez) - p);
    const z1 = g.clampZ(Math.max(sz, ez) + p);
    const qid = ++this.queryId;
    const data = this.data;

    let best = 1;
    let bestSlot = -1;
    let bnx = 0;
    let bny = 1;
    let bnz = 0;
    let bpx = 0;
    let bpy = 0;
    let bpz = 0;

    let stuckSlot = -1;
    let stuckDepth = -1;
    let snx = 0;
    let sny = 1;
    let snz = 0;

    for (let z = z0; z <= z1; z++) {
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const c = g.index(x, y, z);
          const s0 = g.cellStart[c]!;
          const s1 = g.cellStart[c + 1]!;
          for (let k = s0; k < s1; k++) {
            const slot = g.cellItems[k]!;
            if (this.stamp[slot] === qid) continue;
            this.stamp[slot] = qid;
            if (!sweepSolid(data, slot * STRIDE, sx, sy, sz, dx, dy, dz, hc, radius, best))
              continue;
            if (_hitStartSolid) {
              if (_hitDepth > stuckDepth) {
                stuckDepth = _hitDepth;
                stuckSlot = slot;
                snx = _hitNx;
                sny = _hitNy;
                snz = _hitNz;
              }
              continue;
            }
            if (_hitT < best) {
              best = _hitT;
              bestSlot = slot;
              bnx = _hitNx;
              bny = _hitNy;
              bnz = _hitNz;
              bpx = _hitPx;
              bpy = _hitPy;
              bpz = _hitPz;
            }
          }
        }
      }
    }

    if (stuckSlot >= 0) {
      const nl = Math.sqrt(snx * snx + sny * sny + snz * snz);
      const inv = nl > EPSILON ? 1 / nl : 0;
      res.startSolid = true;
      res.hit = true;
      res.fraction = 0;
      res.normal.x = snx * inv;
      res.normal.y = sny * inv;
      res.normal.z = snz * inv;
      res.point.x = sx;
      res.point.y = sy;
      res.point.z = sz;
      res.surfaceId = this.ids[stuckSlot]!;
      res.surface = this.surfaces[stuckSlot]! as SurfaceKind;
      return res;
    }

    if (bestSlot < 0) return res;

    const nl = Math.sqrt(bnx * bnx + bny * bny + bnz * bnz);
    const inv = nl > EPSILON ? 1 / nl : 0;
    res.hit = true;
    res.fraction = best < 0 ? 0 : best > 1 ? 1 : best;
    res.normal.x = bnx * inv;
    res.normal.y = bny * inv;
    res.normal.z = bnz * inv;
    res.point.x = bpx;
    res.point.y = bpy;
    res.point.z = bpz;
    res.surfaceId = this.ids[bestSlot]!;
    res.surface = this.surfaces[bestSlot]! as SurfaceKind;
    return res;
  }
}

/**
 * Build the queryable collision world for an arena. Do this once per match — the
 * broadphase is baked here and every subsequent query is allocation-free.
 */
export function createCollisionWorld(arena: ArenaData): CollisionWorld {
  return new World(arena);
}
